/**
 * Where a floating layer — a tooltip, a menu, a context menu — sits next to
 * what it belongs to, and where it is rendered. `placeFloating` is a pure
 * function of measured rectangles, so the rules can be tested without a
 * browser: the caller renders the layer once hidden, measures it, and asks
 * here before the first paint. `floatingContainer` is the DOM-reading
 * function beside it — the portal target — kept here so every layer picks it
 * by the same rule, and `whenBoxRisen` is its other half: the one moment the
 * target changes what `position: fixed` means.
 *
 * `components/stages/popoverPlacement.ts` is StageControl's own and has a
 * caret and a sticky band to respect; this one is the general helper and
 * leaves that file alone.
 */

/** Never closer to a viewport edge than this (px). */
export const FLOAT_EDGE = 8;
/** Anchor to layer (px). A point anchor has none: a context menu opens on the pointer. */
export const FLOAT_GAP = 6;

export type Side = "above" | "below";
export type Align = "start" | "end" | "center";

export interface Placed {
  left: number;
  top: number;
  /** The side it ended up on — `prefer.side` unless that one had no room. */
  side: Side;
  /** The anchor point relative to the layer's own box, in px: the `transform-origin` it grows from. */
  originX: number;
  originY: number;
}

/** `value` held in `[FLOAT_EDGE, available − extent − FLOAT_EDGE]`; where that range is empty, the near edge wins. */
function keepInside(value: number, extent: number, available: number): number {
  return Math.max(FLOAT_EDGE, Math.min(value, available - extent - FLOAT_EDGE));
}

/** `value` held in `[0, extent]`; never `-0`. */
function within(value: number, extent: number): number {
  return Math.max(0, Math.min(value, extent));
}

/**
 * Below the anchor or above it, lined up by `prefer.align`, then held inside
 * the viewport.
 *
 * Vertically the preferred side wins while the layer fits on it, the other
 * side wins when only that one fits, and when neither does the side with more
 * room wins and the layer is clamped to the edge. `viewport.top` is a ceiling
 * (UX-Gutachten 2, B-1): the masthead's lower edge, under which the room
 * above is counted and the layer is held — a menu clamped to 8 px from the
 * window's edge lay over the steel masthead and looked broken. Where that
 * leaves too little room above, the layer opens below. Horizontally nothing flips:
 * the layer is pulled in from the edge — on a 390-px phone a menu at the
 * right edge simply ends 8 px before it.
 *
 * `anchor` is a `DOMRect` (a trigger) or a point (a context menu's pointer).
 * The origin is the anchor's centre on the edge that faces the layer — or the
 * point itself — relative to the layer, held inside its box, so a layer that
 * was pushed away from where it was meant to open still grows from the right
 * place.
 */
export function placeFloating(
  anchor: DOMRect | { x: number; y: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number; top?: number },
  prefer: { side: Side; align: Align },
): Placed {
  const isPoint = !("width" in anchor);
  const box = isPoint
    ? { left: anchor.x, right: anchor.x, top: anchor.y, bottom: anchor.y }
    : { left: anchor.left, right: anchor.right, top: anchor.top, bottom: anchor.bottom };
  const gap = isPoint ? 0 : FLOAT_GAP;

  const wanted =
    prefer.align === "start"
      ? box.left
      : prefer.align === "end"
        ? box.right - size.width
        : (box.left + box.right) / 2 - size.width / 2;
  const left = keepInside(wanted, size.width, viewport.width);

  // The ceiling: 0 is the window's edge, more is the masthead's lower edge
  // (`floatingCeiling`) — the room above is counted from there.
  const ceiling = viewport.top ?? 0;
  const roomBelow = viewport.height - FLOAT_EDGE - (box.bottom + gap);
  const roomAbove = box.top - gap - (ceiling + FLOAT_EDGE);
  const other: Side = prefer.side === "below" ? "above" : "below";
  const room = (side: Side): number => (side === "below" ? roomBelow : roomAbove);
  const side: Side =
    size.height <= room(prefer.side)
      ? prefer.side
      : size.height <= room(other)
        ? other
        : room(other) > room(prefer.side)
          ? other
          : prefer.side;
  const top = Math.max(
    ceiling + FLOAT_EDGE,
    keepInside(side === "below" ? box.bottom + gap : box.top - gap - size.height, size.height, viewport.height),
  );

  const pointX = isPoint ? anchor.x : (box.left + box.right) / 2;
  const pointY = isPoint ? anchor.y : side === "below" ? box.bottom : box.top;

  return {
    left,
    top,
    side,
    originX: within(pointX - left, size.width),
    originY: within(pointY - top, size.height),
  };
}

/**
 * Where a floating layer is rendered (portalled to). The nearest dialog box
 * around the anchor, otherwise `document.body`: a `Dialog` makes `#root`
 * inert, so a layer on the body would be unreachable from inside it — and
 * for a tooltip, unreadable by a screen reader that honours `inert`.
 */
export function floatingContainer(anchor: Element | null): HTMLElement {
  return anchor?.closest<HTMLElement>('[role="dialog"]') ?? document.body;
}

/** Marks the app's masthead (`AppShell`) — the ceiling of every floating layer on the page. */
export const MASTHEAD_ATTRIBUTE = "data-masthead";

/**
 * The ceiling for a layer rendered in `container` (UX-Gutachten 2, B-1): the
 * masthead's lower edge, in viewport coordinates — `placeFloating` keeps
 * 8 px under it. The window's edge (0) where the masthead has scrolled away,
 * where there is none, in a dialog (whose backdrop lies over the masthead
 * anyway) and for a layer that hangs on the masthead itself.
 */
export function floatingCeiling(anchor: Element | null, container: HTMLElement): number {
  if (container.getAttribute("role") === "dialog") return 0;
  const masthead = document.querySelector(`[${MASTHEAD_ATTRIBUTE}]`);
  if (masthead === null || (anchor !== null && masthead.contains(anchor))) return 0;
  return Math.max(0, masthead.getBoundingClientRect().bottom);
}

/**
 * Calls `replace` when the dialog box a layer is rendered in is done rising in
 * (or gives it up), and returns the function that stops listening. Nothing to
 * wait for when the container is the body.
 *
 * Why: the box enters with a transform (`dialog-rise`, `--dur-4`), and for as
 * long as it carries one it is the containing block of a `position: fixed`
 * layer inside it. A layer placed in that window is measured against the box
 * — the placement subtracts where that starts — but when the animation ends
 * the transform is gone, the same `left`/`top` are read against the viewport,
 * and the layer jumps by where the box had been. Placing it again at that
 * moment puts it back under its anchor. A layer opened after the rise needs
 * nothing, and no event comes.
 *
 * `animationend` and `animationcancel` both: a box whose animation is cut off
 * (display, reduced motion) has dropped its transform just as well. Only the
 * box's own — a descendant's animation bubbles up here and says nothing about it.
 */
export function whenBoxRisen(container: HTMLElement, replace: () => void): () => void {
  if (container.getAttribute("role") !== "dialog") return () => undefined;
  const onEnd = (event: Event) => {
    if (event.target === container) replace();
  };
  container.addEventListener("animationend", onEnd);
  container.addEventListener("animationcancel", onEnd);
  return () => {
    container.removeEventListener("animationend", onEnd);
    container.removeEventListener("animationcancel", onEnd);
  };
}
